import {
  FormControl,
  Grid2,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material'
import { useAuth } from 'components/AuthProvider'
import Button from 'components/Button'
import DatePicker from 'components/DatePicker'
import { kidVaultPath } from 'components/Routes'
import DefaultLayout from 'components/layout/DefaultLayout'
import { Formik } from 'formik'
import { useVault, useMutateUpdateVault } from 'hooks/useInventory'
import { useNavigate, useParams } from 'react-router-dom'
import { API_DATE_FORMAT, UI_DATE_FORMAT, formatDate } from 'utils/util'
import * as Yup from 'yup'
import RemoveKidVault from './RemoveKidVault'
import useStyles from './styles'

type Relationship =
  | 'parent'
  | 'grandparent'
  | 'sibling'
  | 'family'
  | 'extended-family'
  | 'other'
  | ''

interface FormValues {
  name: string
  email?: string
  relationship?: Relationship
  dateOfBirth?: string
}

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Preferred name is required field'),
  email: Yup.string().email()
})

export default function UpdateKidVault(): JSX.Element {
  const { classes } = useStyles()

  const navigate = useNavigate()

  const { refreshCurrentUser, setToast } = useAuth()

  const { id } = useParams<{ id: string }>()

  const { data: vaultData } = useVault(id ? Number(id) : null)

  const initialVault = vaultData

  const updateVaultMutation = useMutateUpdateVault()
  const loading = updateVaultMutation.isLoading

  const initialValues = {
    name: initialVault?.name ?? '',
    dateOfBirth: initialVault?.dateOfBirth
      ? formatDate({
          date: initialVault?.dateOfBirth,
          inputFormat: API_DATE_FORMAT,
          outputFormat: UI_DATE_FORMAT
        })
      : '',
    email: initialVault?.email ?? '',
    relationship: (initialVault?.relationship as Relationship) ?? ''
  }

  const handleSubmit = ({
    name,
    dateOfBirth,
    email,
    relationship
  }: FormValues) => {
    updateVaultMutation.mutate(
      {
        vaultId: Number(id),
        data: {
          name,
          // A date of birth or an email the parent emptied is cleared: null and
          // a blank email both clear on goldie-cloud. An emptied date used to
          // be left out, so it could never be cleared.
          dateOfBirth: dateOfBirth
            ? formatDate({
                date: dateOfBirth,
                inputFormat: UI_DATE_FORMAT,
                outputFormat: API_DATE_FORMAT
              })
            : null,
          email: email?.trim() ?? '',
          relationship
        }
      },
      {
        onSuccess: async () => {
          await refreshCurrentUser()
          setToast({
            message: 'Update kid vault successfully!',
            open: true,
            duration: 3000,
            type: 'success'
          })
          navigate(kidVaultPath())
        },
        onError: (error: unknown) => {
          const err = error as { message?: string }
          setToast({
            message: err?.message ?? 'An error occurred',
            open: true,
            duration: 3000,
            type: 'error'
          })
        }
      }
    )
  }

  return (
    <DefaultLayout backgroundColor='white'>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {({
          handleBlur,
          handleChange,
          handleSubmit,
          values,
          errors,
          touched
        }) => (
          <form onSubmit={handleSubmit}>
            <Grid2 container>
              <Grid2 size={{ xs: 12 }}>
                <Typography
                  fontWeight={700}
                  textAlign='center'
                  textTransform='uppercase'
                  variant='h6'
                >
                  Enter your kid's name
                </Typography>
              </Grid2>

              <Grid2 size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  required
                  type='text'
                  id='name'
                  label='Preferred name'
                  name='name'
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(errors.name && touched.name)}
                  helperText={
                    Boolean(errors.name && touched.name) && errors.name
                  }
                />
              </Grid2>

              <Grid2 mt={4} size={{ xs: 12 }}>
                <Typography fontWeight={700}>Optional Info</Typography>
              </Grid2>

              <Grid2 size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  type='text'
                  id='email'
                  label='Email'
                  name='email'
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(errors.email && touched.email)}
                  helperText={
                    Boolean(errors.email && touched.email)
                      ? errors.email
                      : 'Add email so the caregiver or child can be updated'
                  }
                />
              </Grid2>

              <Grid2 container spacing={2}>
                <Grid2 size={{ xs: 12 }}>
                  <DatePicker<FormValues>
                    name='dateOfBirth'
                    label='Date of birth'
                    inputFormat={UI_DATE_FORMAT}
                    outputFormat={UI_DATE_FORMAT}
                  />
                </Grid2>

                <Grid2 size={{ xs: 12 }}>
                  <FormControl fullWidth>
                    <InputLabel id='relationship' sx={{ left: '0' }}>
                      Your relationship
                    </InputLabel>
                    <Select
                      labelId='relationship'
                      label='Relationship'
                      name='relationship'
                      value={values.relationship}
                      onChange={handleChange}
                      className={classes.select}
                    >
                      <MenuItem value='parent'>Parent</MenuItem>
                      <MenuItem value='grandparent'>Grandparent</MenuItem>
                      <MenuItem value='sibling'>Sibling</MenuItem>
                      <MenuItem value='family'>Family</MenuItem>
                      <MenuItem value='extended-family'>
                        Extended Family
                      </MenuItem>
                      <MenuItem value='other'>Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid2>
              </Grid2>

              <Grid2 mt={8} size={{ xs: 12 }}>
                <Button
                  disabled={loading}
                  color='primary'
                  type='submit'
                  fullWidth
                >
                  Update kid vault
                </Button>
              </Grid2>
            </Grid2>
          </form>
        )}
      </Formik>
      {/* Behind kids_vault_self_remove: renders nothing while it is off. */}
      {initialVault && (
        <RemoveKidVault
          vaultId={Number(id)}
          vaultName={initialVault.name ?? ''}
        />
      )}
    </DefaultLayout>
  )
}
